import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  CardBody,
  FormGroup,
  Label,
  Input,
  Button,
  FormFeedback,
  Alert,
} from 'reactstrap';

import Switch from 'rc-switch';
import 'rc-switch/assets/index.css';
import IntlMessages from '../../../../../../helpers/IntlMessages';
import { Colxx } from '../../../../../../components/common/CustomBootstrap';

// VALIDATION
const GA4_ID_REGEX = /^G-[A-Z0-9]{6,15}$/i;

// DEFAULT CONFIG
const DEFAULT_GA4_CONFIG = {
  enabled: false,
  measurementId: '',
  events: {
    avatarClick: {
      enabled: true,
      tag: 'chat_avatar_click',
    },
    menuClick: {
      enabled: true,
      tagPrefix: 'chat_menu',
    },
    chatStarted: {
      enabled: true,
      tag: 'chat_started',
    },
  },
};

const HelpCenterGA4Tracking = ({ botSettings, saveIntegration }) => {
  const ga4Settings = botSettings?.gA4Tracking || DEFAULT_GA4_CONFIG;

  // MAIN SETTINGS
  const [enabled, setEnabled] = useState(ga4Settings.enabled || false);
  const [measurementId, setMeasurementId] = useState(
    ga4Settings.measurementId || ''
  );

  // VALIDATION STATES
  const [idError, setIdError] = useState('');
  const [generalError, setGeneralError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [idTouched, setIdTouched] = useState(false);
  const [saving, setSaving] = useState(false);

  // EVENT STATES
  const [avatarEnabled, setAvatarEnabled] = useState(
    ga4Settings.events?.avatarClick?.enabled ?? true
  );
  const [avatarTag, setAvatarTag] = useState(
    ga4Settings.events?.avatarClick?.tag || 'chat_avatar_click'
  );

  const [menuEnabled, setMenuEnabled] = useState(
    ga4Settings.events?.menuClick?.enabled ?? true
  );
  const [menuTag, setMenuTag] = useState(
    ga4Settings.events?.menuClick?.tagPrefix || 'chat_menu'
  );

  const [chatStartedEnabled, setChatStartedEnabled] = useState(
    ga4Settings.events?.chatStarted?.enabled ?? true
  );
  const [chatStartedTag, setChatStartedTag] = useState(
    ga4Settings.events?.chatStarted?.tag || 'chat_started'
  );

  // SYNC SETTINGS
  useEffect(() => {
    const settings = botSettings?.gA4Tracking || DEFAULT_GA4_CONFIG;

    setEnabled(settings.enabled || false);
    setMeasurementId(settings.measurementId || '');
    setAvatarEnabled(settings.events?.avatarClick?.enabled ?? true);
    setAvatarTag(settings.events?.avatarClick?.tag || 'chat_avatar_click');
    setMenuEnabled(settings.events?.menuClick?.enabled ?? true);
    setMenuTag(settings.events?.menuClick?.tagPrefix || 'chat_menu');
    setChatStartedEnabled(settings.events?.chatStarted?.enabled ?? true);
    setChatStartedTag(settings.events?.chatStarted?.tag || 'chat_started');

    // Reset validation states
    setIdError('');
    setGeneralError('');
    setSuccessMsg('');
    setIdTouched(false);
  }, [botSettings]);

  // VALIDATE GA4 ID
  const validateGA4Id = useCallback((id) => {
    if (!id || !id.trim()) return false;
    return GA4_ID_REGEX.test(id.trim());
  }, []);

  // SANITIZE TAGS
  const sanitizeTag = useCallback((value) => {
    return value
      .trim()
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '');
  }, []);

  // REAL TIME VALIDATION - only when enabled and touched
  useEffect(() => {
    if (!idTouched) {
      setIdError('');
      return;
    }

    if (!measurementId.trim()) {
      setIdError('GA4 Measurement ID is required when tracking is enabled.');
      return;
    }

    if (!validateGA4Id(measurementId)) {
      setIdError('Invalid GA4 ID format. Example: G-ABCD123456');
      return;
    }

    setIdError('');
  }, [measurementId, idTouched, validateGA4Id]);

  // SAVE
  const handleSave = () => {
    setIdTouched(true);
    setGeneralError('');
    setSuccessMsg('');

    // VALIDATION: If enabled, must have valid ID
    if (enabled) {
      if (!measurementId.trim()) {
        setIdError('GA4 Measurement ID is required when tracking is enabled.');
        return;
      }

      if (!validateGA4Id(measurementId)) {
        setIdError('Invalid GA4 ID format. Example: G-ABCD123456');
        return;
      }

      // At least one event must be enabled
      if (!avatarEnabled && !menuEnabled && !chatStartedEnabled) {
        setGeneralError(
          'Please enable at least one tracking event when GA4 is active.'
        );
        return;
      }
    }

    // Validate tag names for enabled events
    if (enabled) {
      if (avatarEnabled && !sanitizeTag(avatarTag)) {
        setGeneralError('Avatar Click tag is required.');
        return;
      }

      if (menuEnabled && !sanitizeTag(menuTag)) {
        setGeneralError('Menu Click prefix is required.');
        return;
      }

      if (chatStartedEnabled && !sanitizeTag(chatStartedTag)) {
        setGeneralError('Chat Started tag is required.');
        return;
      }
    }

    try {
      setSaving(true);
      const payload = {
        botSetting: {
          ...botSettings,
          gA4Tracking: {
            enabled,
            measurementId: measurementId.trim(),
            events: {
              avatarClick: {
                enabled: avatarEnabled,
                tag: sanitizeTag(avatarTag) || 'chat_avatar_click',
              },
              menuClick: {
                enabled: menuEnabled,
                tagPrefix: sanitizeTag(menuTag) || 'chat_menu',
              },
              chatStarted: {
                enabled: chatStartedEnabled,
                tag: sanitizeTag(chatStartedTag) || 'chat_started',
              },
            },
          },
        },
      };

      saveIntegration(payload);

      setSuccessMsg('GA4 settings saved successfully.');

      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (error) {
      setGeneralError('Something went wrong while saving settings.');
    } finally {
      setSaving(false);
    }
  };

  const isSaveDisabled = saving || (enabled && !!idError);
  const sectionDisabled = !enabled;

  return (
    <Card>
      <CardBody>
        <h2 className="mb-2">
          <IntlMessages id="integration.google_analytics" />
        </h2>

        <p className="text-muted mb-4">
          <IntlMessages id="integration.google_analytics_desc" />
        </p>

        {/* ENABLE TOGGLE */}
        <FormGroup>
          <Colxx md="12" className="mb-4 d-flex align-items-center">
            <Switch
              className="agentswitch custom-switch-primary custom-switch-small"
              checked={enabled}
              onChange={(checked) => {
                setEnabled(checked);
                if (!checked) {
                  setIdError('');
                  setIdTouched(false);
                }
              }}
            />
            <div className="ml-3">
              <p className="mb-0" style={{ fontWeight: 600 }}>
                <IntlMessages id="integration.enable_ga4" />
              </p>
            </div>
          </Colxx>
        </FormGroup>

        {/* GA4 ID */}
        <FormGroup>
          <Label for="ga4-id">
            <IntlMessages id="integration.ga4_measurement_id" />
          </Label>
          <Input
            id="ga4-id"
            type="text"
            placeholder="G-XXXXXXXXXX"
            value={measurementId}
            onChange={(e) => {
              setMeasurementId(e.target.value.toUpperCase());
              setIdTouched(true);
            }}
            invalid={idTouched && !!idError}
            valid={idTouched && !idError && measurementId.length > 0}
          />
          {enabled && idTouched && idError && (
            <FormFeedback style={{ display: 'block' }}>{idError}</FormFeedback>
          )}
          <small className="form-text text-muted">
            <IntlMessages id="integration.ga4_id_example" />
          </small>
        </FormGroup>

        {/* TRACK EVENTS */}
        <div className="mt-5 mb-3">
          <h4>
            <IntlMessages id="integration.track_events" />
          </h4>
          <p className="text-muted text-small">
            <IntlMessages id="integration.track_events_desc" />
          </p>
        </div>

        <div
          style={{
            opacity: sectionDisabled ? 0.5 : 1,
            pointerEvents: sectionDisabled ? 'none' : 'auto',
          }}
        >
          {/* AVATAR CLICK */}
          <Card className="mb-3 bg-light">
            <CardBody>
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h5 className="mb-1">
                    <IntlMessages id="integration.avatar_click" />
                  </h5>
                  <p className="text-muted mb-0">
                    <IntlMessages id="integration.avatar_click_desc" />
                  </p>
                </div>
                <Switch
                  checked={avatarEnabled}
                  onChange={setAvatarEnabled}
                  disabled={sectionDisabled}
                />
              </div>

              <div className="mt-3">
                <Label>
                  <IntlMessages id="integration.event_tag" />
                </Label>
                <Input
                  type="text"
                  value={avatarTag}
                  onChange={(e) => setAvatarTag(e.target.value)}
                  disabled={!avatarEnabled || sectionDisabled}
                />
                <small className="text-muted">
                  <IntlMessages id="integration.final_tag" />:{' '}
                  <code>{sanitizeTag(avatarTag)}</code>
                </small>
              </div>
            </CardBody>
          </Card>

          {/* MENU CLICK */}
          <Card className="mb-3 bg-light">
            <CardBody>
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h5 className="mb-1">
                    <IntlMessages id="integration.menu_click" />
                  </h5>
                  <p className="text-muted mb-0">
                    <IntlMessages id="integration.menu_click_desc" />
                  </p>
                </div>
                <Switch
                  checked={menuEnabled}
                  onChange={setMenuEnabled}
                  disabled={sectionDisabled}
                />
              </div>

              <div className="mt-3">
                <Label>
                  <IntlMessages id="integration.event_prefix" />
                </Label>
                <Input
                  type="text"
                  value={menuTag}
                  onChange={(e) => setMenuTag(e.target.value)}
                  disabled={!menuEnabled || sectionDisabled}
                />
                <small className="text-muted">
                  <IntlMessages id="integration.example_event" />:{' '}
                  <code>{sanitizeTag(menuTag)}_start_chat</code>
                </small>
              </div>
            </CardBody>
          </Card>

          {/* CHAT STARTED */}
          <Card className="mb-3 bg-light">
            <CardBody>
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  <h5 className="mb-1">
                    <IntlMessages id="integration.chat_started" />
                  </h5>
                  <p className="text-muted mb-0">
                    <IntlMessages id="integration.chat_started_desc" />
                  </p>
                </div>
                <Switch
                  checked={chatStartedEnabled}
                  onChange={setChatStartedEnabled}
                  disabled={sectionDisabled}
                />
              </div>

              <div className="mt-3">
                <Label>
                  <IntlMessages id="integration.event_tag" />
                </Label>
                <Input
                  type="text"
                  value={chatStartedTag}
                  onChange={(e) => setChatStartedTag(e.target.value)}
                  disabled={!chatStartedEnabled || sectionDisabled}
                />
                <small className="text-muted">
                  <IntlMessages id="integration.final_tag" />:{' '}
                  <code>{sanitizeTag(chatStartedTag)}</code>{' '}
                  <span className="text-info">
                    (<IntlMessages id="integration.once_per_session" />)
                  </span>
                </small>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* SUCCESS MESSAGE */}
        {successMsg && (
          <Alert color="success" className="mt-3">
            {successMsg}
          </Alert>
        )}

        {/* GENERAL ERROR */}
        {generalError && (
          <div
            className="mt-3 px-3 py-2"
            style={{
              background: '#fff3cd',
              border: '1px solid #ffeeba',
              borderRadius: '8px',
              color: '#856404',
              fontSize: '14px',
              fontWeight: 500,
            }}
          >
            {generalError}
          </div>
        )}

        {/* SAVE BUTTON */}
        <Button
          color="primary"
          className="mt-4"
          onClick={handleSave}
          disabled={isSaveDisabled}
        >
          {saving ? (
            <IntlMessages id="integration.saving" />
          ) : (
            <IntlMessages id="integration.save_ga4_settings" />
          )}
        </Button>
      </CardBody>
    </Card>
  );
};

export default HelpCenterGA4Tracking;
